import { onUnmounted, reactive } from 'vue'
import {
  sendEmailVerification,
  sendPhoneVerification,
  verifyEmailVerification,
  verifyPhoneVerification,
} from '@/api/verification'
import type { VerificationPurpose } from '@/types/verification'

const DEFAULT_EXPIRE_SECONDS = 180
const DEFAULT_RESEND_SECONDS = 60

type SendError = Error & { waitSeconds?: number }

export type VerificationState = {
  codeSent: boolean
  verified: boolean
  sending: boolean
  verifying: boolean
  errorMessage: string
  timer: number
  resendIn: number
  formatTimer: (seconds: number) => string
  sendActionLabel: (idleLabel: string, againLabel: string) => string
  reset: () => void
  sendEmail: (email: string) => Promise<void>
  verifyEmail: (email: string, code: string) => Promise<void>
  sendPhone: (phone: string) => Promise<void>
  verifyPhone: (phone: string, code: string) => Promise<void>
}

export function useVerification(purpose: VerificationPurpose): VerificationState {
  let timerId: ReturnType<typeof setInterval> | null = null

  const clearTimer = () => {
    if (timerId) {
      clearInterval(timerId)
      timerId = null
    }
  }

  const ensureTick = () => {
    if (timerId) return
    timerId = setInterval(() => {
      if (verification.timer > 0) verification.timer -= 1
      if (verification.resendIn > 0) verification.resendIn -= 1
      if (verification.timer <= 0 && verification.resendIn <= 0) {
        clearTimer()
      }
    }, 1000)
  }

  const startExpiryTimer = (seconds: number) => {
    verification.timer = Math.max(0, Math.trunc(seconds) || DEFAULT_EXPIRE_SECONDS)
    ensureTick()
  }

  const startResendCooldown = (seconds: number) => {
    verification.resendIn = Math.max(0, Math.trunc(seconds) || DEFAULT_RESEND_SECONDS)
    ensureTick()
  }

  const waitSecondsOf = (error: unknown) => {
    const wait = (error as SendError)?.waitSeconds
    return typeof wait === 'number' && wait > 0 ? wait : 0
  }

  const verification = reactive<VerificationState>({
    codeSent: false,
    verified: false,
    sending: false,
    verifying: false,
    errorMessage: '',
    timer: 0,
    resendIn: 0,

    formatTimer(seconds: number) {
      const min = Math.floor(seconds / 60)
      const sec = seconds % 60
      return `${min}:${String(sec).padStart(2, '0')}`
    },

    sendActionLabel(idleLabel: string, againLabel: string) {
      if (verification.sending) return '보내는 중'
      if (verification.resendIn > 0) return `${verification.resendIn}초 후 다시`
      if (verification.codeSent) return againLabel
      return idleLabel
    },

    reset() {
      verification.codeSent = false
      verification.verified = false
      verification.errorMessage = ''
      clearTimer()
      verification.timer = 0
      verification.resendIn = 0
    },

    async sendEmail(email: string) {
      verification.sending = true
      verification.errorMessage = ''
      try {
        const result = await sendEmailVerification(email.trim(), purpose)
        verification.codeSent = true
        verification.verified = false
        startExpiryTimer(result.data?.expiresIn ?? DEFAULT_EXPIRE_SECONDS)
        startResendCooldown(result.data?.resendIn ?? DEFAULT_RESEND_SECONDS)
      } catch (error) {
        const wait = waitSecondsOf(error)
        if (wait) startResendCooldown(wait)
        verification.errorMessage =
          error instanceof Error ? error.message : '이메일 인증번호 발송에 실패했습니다.'
        throw error
      } finally {
        verification.sending = false
      }
    },

    async verifyEmail(email: string, code: string) {
      verification.verifying = true
      verification.errorMessage = ''
      try {
        await verifyEmailVerification(email.trim(), code.trim(), purpose)
        verification.verified = true
        clearTimer()
        verification.timer = 0
        verification.resendIn = 0
      } catch (error) {
        verification.errorMessage =
          error instanceof Error ? error.message : '이메일 인증에 실패했습니다.'
        throw error
      } finally {
        verification.verifying = false
      }
    },

    async sendPhone(phone: string) {
      verification.sending = true
      verification.errorMessage = ''
      try {
        const result = await sendPhoneVerification(phone.trim(), purpose)
        verification.codeSent = true
        verification.verified = false
        startExpiryTimer(result.data?.expiresIn ?? DEFAULT_EXPIRE_SECONDS)
        startResendCooldown(result.data?.resendIn ?? DEFAULT_RESEND_SECONDS)
      } catch (error) {
        const wait = waitSecondsOf(error)
        if (wait) startResendCooldown(wait)
        verification.errorMessage =
          error instanceof Error ? error.message : '문자 인증번호 발송에 실패했습니다.'
        throw error
      } finally {
        verification.sending = false
      }
    },

    async verifyPhone(phone: string, code: string) {
      verification.verifying = true
      verification.errorMessage = ''
      try {
        await verifyPhoneVerification(phone.trim(), code.trim(), purpose)
        verification.verified = true
        clearTimer()
        verification.timer = 0
        verification.resendIn = 0
      } catch (error) {
        verification.errorMessage =
          error instanceof Error ? error.message : '휴대폰 인증에 실패했습니다.'
        throw error
      } finally {
        verification.verifying = false
      }
    },
  })

  onUnmounted(() => {
    clearTimer()
  })

  return verification
}
